<template>
  <Viewport
    :document-id="documentId"
    class="m-pdf-viewer__viewport"
    :class="{ 'm-pdf-viewer__viewport--no-scroll': singlePage }"
    @pointerdown.capture="swallowNonPrimary"
    @mousedown.capture="swallowNonPrimary"
    @contextmenu="handleContextMenu"
  >
    <GlobalPointerProvider>
      <Scroller :document-id="documentId">
        <template #default="{ page }">
          <PagePointerProvider :document-id="documentId" :page-index="page.pageIndex">
            <RenderLayer :document-id="documentId" :page-index="page.pageIndex" />
            <!-- ENG-20867 triangle pointing to the focused field -->
            <svg
              v-if="showHighlight && highlightGeom && highlightGeom.pageIndex === page.pageIndex"
              class="m-pdf-viewer__field-highlight"
              width="100%"
              height="100%"
              style="
                position: absolute;
                top: 0;
                left: 0;
                pointer-events: none;
                overflow: visible;
                z-index: 10;
              "
            >
              <polygon
                :points="highlightGeom.triangle"
                fill="white"
                stroke="white"
                stroke-width="3"
                stroke-linejoin="round"
              />
              <polygon :points="highlightGeom.triangle" fill="#ef4444" />
            </svg>
            <SelectionLayer
              v-if="selectable"
              :document-id="documentId"
              :page-index="page.pageIndex"
            />
          </PagePointerProvider>
        </template>
      </Scroller>
    </GlobalPointerProvider>
  </Viewport>
  <Teleport to="body">
    <div
      v-if="contextMenu"
      class="m-pdf-viewer__context-menu"
      :style="{ left: `${contextMenu.x}px`, top: `${contextMenu.y}px` }"
      role="menu"
    >
      <button type="button" role="menuitem" @click="copyAndClose">Copy</button>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { Viewport, useViewportCapability } from "@embedpdf/plugin-viewport/vue";
import { Scroller, useScroll } from "@embedpdf/plugin-scroll/vue";
import { RenderLayer } from "@embedpdf/plugin-render/vue";
import { SelectionLayer, useSelectionCapability } from "@embedpdf/plugin-selection/vue";
import {
  GlobalPointerProvider,
  PagePointerProvider,
} from "@embedpdf/plugin-interaction-manager/vue";
import { useZoom } from "@embedpdf/plugin-zoom/vue";

interface HighlightTarget {
  pageIndex: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

const props = withDefaults(
  defineProps<{
    documentId: string;
    url: string;
    singlePage?: boolean;
    selectable?: boolean;
    // Coordinate (PDF point space) of the field to point at + scroll to.
    highlightTarget?: HighlightTarget | null;
    showHighlight?: boolean;
    autoScroll?: boolean;
  }>(),
  { selectable: true, showHighlight: true, autoScroll: true },
);

const { provides: selectionCap } = useSelectionCapability();
const { provides: viewportCap } = useViewportCapability();
const { provides: scrollScope } = useScroll(() => props.documentId);
const { state: zoomState } = useZoom(() => props.documentId);

const highlightRect = ref<{ pageIndex: number; rect: any } | null>(null);
// Increment on every placement; stale async completions check this and bail.
let searchGen = 0;

const highlightGeom = computed(() => {
  if (!highlightRect.value) return null;
  const z = zoomState.value.currentZoomLevel;
  const r = highlightRect.value.rect;
  // tip is 10px to the left of the field's left edge
  const tipX = Math.max(14, r.origin.x * z - 10);
  const baseX = tipX - 12; // 12px deep arrowhead
  const midY = (r.origin.y + r.size.height / 2) * z;
  return {
    pageIndex: highlightRect.value.pageIndex,
    // triangle: base at baseX ±6px, tip at tipX
    triangle: `${baseX},${midY - 6} ${tipX},${midY} ${baseX},${midY + 6}`,
  };
});

async function waitForViewportMount(vp: any, docId: string, tries = 50) {
  for (let i = 0; i < tries; i++) {
    if (vp.isViewportMounted(docId)) return true;
    await new Promise((r) => setTimeout(r, 60));
  }
  return false;
}

// Poll a getter until it returns a truthy value (or we give up). The
// doc-scoped capabilities resolve a few ticks after (re)mount, so a placement
// kicked off by the immediate watch on mount must wait for them rather than
// bail — otherwise a PDF reload (remount) never re-highlights the field.
async function waitFor<T>(get: () => T, tries = 50): Promise<T | null> {
  for (let i = 0; i < tries; i++) {
    const v = get();
    if (v) return v;
    await new Promise((r) => setTimeout(r, 60));
  }
  return null;
}

// Place the highlight on the target field's box (in PDF point space) and,
// when autoScroll is on, scroll it into view. The box coordinates come from
// the template definition keyed by the field, so no text search is needed.
async function placeHighlight(target: HighlightTarget | null | undefined): Promise<void> {
  // Bump generation so any in-flight placement is cancelled before we await.
  const myGen = ++searchGen;
  if (!target) {
    highlightRect.value = null;
    return;
  }
  const rect = {
    origin: { x: target.x, y: target.y },
    size: { width: target.width, height: target.height },
  };
  highlightRect.value = { pageIndex: target.pageIndex, rect };
  if (!props.autoScroll) return;
  // Wait for the doc-scoped capabilities — on (re)mount they resolve a tick
  // after this runs from the immediate watch.
  const scroll = await waitFor(() => scrollScope.value);
  const vp = await waitFor(() => viewportCap.value);
  if (searchGen !== myGen) return; // superseded while waiting
  if (!scroll || !vp) return;
  await waitForViewportMount(vp, props.documentId);
  if (searchGen !== myGen) return;
  const docVp = vp.forDocument(props.documentId);
  // After a (re)mount the page images lay out progressively, so the scroll
  // container isn't tall enough yet to honour a deep scroll — a single
  // scrollTo to a far-down field clamps to 0. Re-apply over ~1s so the
  // target position sticks once the layout has grown enough.
  for (let i = 0; i < 14; i++) {
    const pos = scroll.getRectPositionForPage(target.pageIndex, rect);
    if (pos) {
      docVp.scrollTo({ x: pos.origin.x, y: pos.origin.y, alignY: 30, behavior: "auto" });
    }
    await new Promise((r) => setTimeout(r, 80));
    if (searchGen !== myGen) return; // a newer placement took over
  }
}

watch(
  [() => props.highlightTarget, scrollScope, viewportCap],
  ([target]) => placeHighlight(target),
  { immediate: true, flush: "sync", deep: true },
);

// The selection plugin only runs its text-selection logic for interaction
// modes that have opted in. The default mode is `pointerMode`, which is
// otherwise selection-free — enable text selection on it as soon as the
// capability resolves and re-enable on each doc swap.
watch(
  [selectionCap, () => props.documentId, () => props.selectable],
  ([sel, docId, on]) => {
    if (!sel || !docId) return;
    sel.enableForMode("pointerMode", { enableSelection: !!on, showSelectionRects: !!on }, docId);
  },
  { immediate: true },
);

// The selection plugin exposes copyToClipboard() but doesn't listen for
// the native Cmd/Ctrl+C event itself — wire it here. The shipped
// CopyToClipboard helper component then forwards the resulting event to
// navigator.clipboard.writeText(text).
function handleCopy(e: ClipboardEvent) {
  const sel = selectionCap.value;
  if (!sel || !props.selectable) return;
  const state = sel.getState(props.documentId);
  if (!state?.selection) return;
  e.preventDefault();
  sel.copyToClipboard(props.documentId);
}
onMounted(() => document.addEventListener("copy", handleCopy));
onBeforeUnmount(() => document.removeEventListener("copy", handleCopy));

// EmbedPDF's selection plugin clears the selection on every pointerdown
// without filtering by button — right-click would deselect. Swallow
// non-primary-button pointer/mousedowns at capture phase before the
// plugin sees them. The plugin maps both `pointerdown` AND `mousedown`
// to the same onPointerDown handler so we have to block both.

function swallowNonPrimary(e: MouseEvent) {
  if (e.button !== 0) {
    e.stopPropagation();
    e.stopImmediatePropagation();
  }
}

// PDF selection isn't a DOM selection, so the browser's native context
// menu has nothing to offer for it. Render our own "Copy" menu at the
// cursor when the user right-clicks with a live selection.
const contextMenu = ref<{ x: number; y: number } | null>(null);

function handleContextMenu(e: MouseEvent) {
  const sel = selectionCap.value;
  if (!sel || !props.selectable) return;
  const state = sel.getState(props.documentId);
  if (!state?.selection) return;
  e.preventDefault();
  contextMenu.value = { x: e.clientX, y: e.clientY };
}

function closeContextMenu() {
  contextMenu.value = null;
}

// Don't close on clicks inside the menu — otherwise the menu unmounts on
// the Copy button's own pointerdown, before its @click ever fires.
function handleOutsidePointerDown(e: PointerEvent) {
  const target = e.target as Element | null;
  if (target?.closest(".m-pdf-viewer__context-menu")) return;
  closeContextMenu();
}

function copyAndClose() {
  selectionCap.value?.copyToClipboard(props.documentId);
  closeContextMenu();
}

onMounted(() => {
  document.addEventListener("pointerdown", handleOutsidePointerDown);
  document.addEventListener("scroll", closeContextMenu, true);
});
onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", handleOutsidePointerDown);
  document.removeEventListener("scroll", closeContextMenu, true);
});

// NB: the URL prop changing remounts this whole component — MPdfViewer keys
// <EmbedPDF> on the URL (ENG-20867). On remount the searchTarget watch above
// fires (immediate) once the doc-scoped capabilities resolve, re-running the
// search and scrolling to / highlighting the focused field on the new PDF.
</script>

<style>
/* unscoped — context menu teleports to <body> outside the component tree */
.m-pdf-viewer__context-menu {
  position: fixed;
  z-index: 10000;
  background: #fff;
  border: 1px solid var(--border-colour, #d0d0d0);
  border-radius: 6px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
  padding: 4px;
  min-width: 140px;
}

.m-pdf-viewer__context-menu button {
  display: block;
  width: 100%;
  padding: 6px 12px;
  background: transparent;
  border: 0;
  text-align: left;
  font: inherit;
  cursor: pointer;
  border-radius: 4px;
}

.m-pdf-viewer__context-menu button:hover,
.m-pdf-viewer__context-menu button:focus {
  background: var(--grey-lightest, #f2f3f5);
  outline: none;
}
</style>

<style scoped>
.m-pdf-viewer__viewport {
  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: #fff;
}

.m-pdf-viewer__viewport--no-scroll {
  background: transparent;
}

.m-pdf-viewer__viewport--no-scroll::-webkit-scrollbar {
  display: none;
}

/* The PDF page image must not capture the drag gesture — otherwise the
   browser starts a native image-drag and the SelectionLayer never sees
   the pointer events, so text selection never engages. */
.m-pdf-viewer__viewport :deep(img) {
  user-select: none;
  -webkit-user-drag: none;
  -webkit-user-select: none;
  pointer-events: none;
}
</style>
